<template>
  <div class="production production-control">
    <!-- Selector de rango de fechas -->
    <div class="date-range-wrapper">
      <date-range-picker
        ref="picker"
        v-model="dateRange"
        :locale-data="locale"
        :ranges="ranges"
        :show-week-numbers="false"
        :show-dropdowns="true"
        :auto-apply="false"
        :time-picker="true"
        :time-picker24-hour="true"
        opens="right"
        @update="onDateRangeApply"
      >
        <template v-slot:input>
          <button class="date-range-btn">
            📅
            <span class="date-range-label">{{ dateRangeLabel }}</span>
          </button>
        </template>
      </date-range-picker>
      <button
        class="filter-open-btn"
        :class="{ active: hasActiveFilters }"
        @click="openFilterDialog"
      >
        🔎 Filtrar
        <span v-if="hasActiveFilters" class="filter-open-btn-badge">
          {{ activeFilterCount }}
        </span>
      </button>
    </div>

    <div class="records-summary">
      <span class="records-summary-count"
        >{{ totalControlRecords }} REGISTRO(S)</span
      >
    </div>

    <div class="table-container">
      <div class="control-export-mobile-actions">
        <button
          @click="exportToCSV"
          class="mobile-export-btn mobile-export-btn--compact"
        >
          📄 CSV
        </button>
        <button
          @click="exportToExcel"
          class="mobile-export-btn mobile-export-btn--compact"
        >
          📊 Excel
        </button>
      </div>
      <div class="control-export-desktop-actions">
        <button @click="exportToCSV" class="desktop-export-btn">📄 CSV</button>
        <button @click="exportToExcel" class="desktop-export-btn">
          📊 Excel
        </button>
      </div>
      <div class="vuetable-body-wrapper">
        <vuetable
          ref="vuetable"
          :api-mode="false"
          :fields="fields"
          :sort-order="sortOrder"
          :show-sort-icons="false"
          :per-page="perPage"
          :data-manager="dataManager"
          pagination-path="pagination"
          track-by="id"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:row-clicked="onRowClick"
          @vuetable:sort-order-changed="onSortOrderChanged"
        />
      </div>
    </div>
    <div
      v-if="tablePagination && tablePagination.last_page > 1"
      class="control-pagination"
    >
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page <= 1"
        @click="goToPage(1)"
      >
        &lt;&lt;
      </button>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page <= 1"
        @click="goToPage(tablePagination.current_page - 1)"
      >
        &lt;
      </button>
      <span class="pagination-status">
        Pagina {{ tablePagination.current_page }} de
        {{ tablePagination.last_page }}
      </span>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page >= tablePagination.last_page"
        @click="goToPage(tablePagination.current_page + 1)"
      >
        &gt;
      </button>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page >= tablePagination.last_page"
        @click="goToPage(tablePagination.last_page)"
      >
        &gt;&gt;
      </button>
    </div>

    <!-- Diálogo de filtros -->
    <dialog ref="filterDialog" class="filter-dialog">
      <h2 class="dialog-title">Filtrar registros</h2>

      <div class="filter-grid">
        <div class="edit-section">
          <label class="edit-label">Finca:</label>
          <select v-model="filterDraft.farmlandId" class="edit-input">
            <option value="">Todas</option>
            <option
              v-for="fl in sortedFilterFarmlands"
              :key="fl.id"
              :value="String(fl.id)"
            >
              {{ fl.name }}
            </option>
          </select>
        </div>

        <div class="edit-section">
          <label class="edit-label">Invernadero (Zona):</label>
          <select v-model="filterDraft.zoneId" class="edit-input">
            <option value="">Todos</option>
            <option
              v-for="zone in filteredZoneOptions"
              :key="zone.id"
              :value="String(zone.id)"
            >
              {{ zone.name }} ({{ zone.code }})
            </option>
          </select>
        </div>

        <div class="edit-section">
          <label class="edit-label">Modo:</label>
          <select v-model="filterDraft.productionMode" class="edit-input">
            <option value="">Todos</option>
            <option
              v-for="mode in sortedFilterModes"
              :key="mode.code"
              :value="mode.code"
            >
              {{ mode.name }}
            </option>
          </select>
        </div>

        <div class="edit-section">
          <label class="edit-label">Categoría:</label>
          <select v-model="filterDraft.category" class="edit-input">
            <option value="">Todas</option>
            <option
              v-for="category in sortedFilterCategories"
              :key="category.code"
              :value="category.code"
            >
              {{ category.name }}
            </option>
          </select>
        </div>

        <div class="edit-section">
          <label class="edit-label">Formato:</label>
          <select v-model="filterDraft.formatId" class="edit-input">
            <option value="">Todos</option>
            <option
              v-for="format in filteredFormatOptions"
              :key="format.id"
              :value="String(format.id)"
            >
              {{ format.name }}
            </option>
          </select>
        </div>

        <div class="edit-section">
          <label class="edit-label">Usuario creador:</label>
          <input
            type="text"
            v-model.trim="filterDraft.createdBy"
            class="edit-input"
            placeholder="Ej: Albertofdp"
          />
        </div>

        <div class="edit-section">
          <label class="edit-label">Últ. modificación por:</label>
          <input
            type="text"
            v-model.trim="filterDraft.lastModifiedBy"
            class="edit-input"
            placeholder="Nombre o usuario"
          />
        </div>
      </div>

      <div class="filter-actions">
        <button class="filter-clear-btn" @click="clearFilters">Limpiar</button>
        <button class="filter-cancel-btn" @click="closeFilterDialog">
          Cancelar
        </button>
        <button class="filter-apply-btn" @click="applyFilters">
          Aplicar filtros
        </button>
      </div>
    </dialog>

    <!-- Diálogo de opciones -->
    <dialog ref="optionsDialog">
      <h2 class="dialog-title">¿Qué deseas hacer?</h2>
      <div class="icon-options">
        <div class="icon-wrapper">
          <img
            :src="cancelIcon"
            alt="Cancelar"
            class="icon cancel"
            @click="closeDialog"
          />
          <p class="icon-label">Cancelar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="editIcon"
            alt="Editar"
            class="icon edit"
            @click="openEditDialog"
          />
          <p class="icon-label">Editar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="deleteIcon"
            alt="Eliminar"
            class="icon eliminar"
            @click="confirmDelete"
          />
          <p class="icon-label">Eliminar</p>
        </div>
      </div>
    </dialog>

    <!-- Diálogo de edición -->
    <dialog ref="editDialog">
      <h2 class="dialog-title">Editar campos</h2>

      <!-- Selector de finca: staff y owners con más de una finca -->
      <div class="edit-section" v-if="canChangeFarmland">
        <label class="edit-label">Finca:</label>
        <select v-model="editFarmlandId" class="edit-input">
          <option v-for="fl in availableFarmlands" :key="fl.id" :value="fl.id">
            {{ fl.name }}
          </option>
        </select>
      </div>

      <!-- Selector de zona: todos los usuarios -->
      <div class="edit-section" v-if="availableZones.length">
        <label class="edit-label">Zona:</label>
        <select v-model="editZoneId" class="edit-input">
          <option :value="null">— Sin zona —</option>
          <option
            v-for="zone in availableZones"
            :key="zone.id"
            :value="zone.id"
          >
            {{ zone.name }} ({{ zone.code }})
          </option>
        </select>
      </div>

      <!-- Solo para fincas cooperativas -->
      <div class="edit-section" v-if="editFarmType === 'COOPERATIVE'">
        <label class="edit-label">Modo de Producción:</label>
        <select v-model="editProductionMode" class="edit-input">
          <option
            v-for="mode in availableModes"
            :key="mode.code"
            :value="mode.code"
          >
            {{ mode.name }}
          </option>
        </select>
      </div>

      <div
        class="edit-section"
        v-if="editFarmType === 'COOPERATIVE' && availableCategories.length"
      >
        <label class="edit-label">Categoría:</label>
        <select v-model="editCategory" class="edit-input">
          <option
            v-for="category in availableCategories"
            :key="category.code"
            :value="category.code"
          >
            {{ category.name }}
          </option>
        </select>
      </div>

      <div class="edit-section" v-if="isEditDefectCategory">
        <label class="edit-label">Subcategorías:</label>
        <div class="subcategory-options">
          <label
            v-for="subcategory in editSubcategoryOptions"
            :key="subcategory"
            class="subcategory-option"
          >
            <input
              type="checkbox"
              :value="subcategory"
              v-model="editSubcategories"
            />
            <span>{{ subcategory }}</span>
          </label>
        </div>
      </div>

      <!-- Formato: mostrar para comerciales siempre, para cooperativas solo si el modo requiere formato -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COMMERCIAL' ||
          (editFarmType === 'COOPERATIVE' &&
            editModeObj &&
            editModeObj.requires_format &&
            !isEditDefectCategory)
        "
      >
        <label class="edit-label">Introducir Formato:</label>
        <select v-model="editFormatId" class="edit-input">
          <option v-for="format in formats" :key="format.id" :value="format.id">
            {{ format.name }}
          </option>
        </select>
      </div>

      <!-- Unidades -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COMMERCIAL' ||
          (editFarmType === 'COOPERATIVE' &&
            editModeObj &&
            editModeObj.requires_format &&
            !isEditDefectCategory)
        "
      >
        <label class="edit-label">Número de cajas:</label>
        <input type="number" v-model="editUnits" min="1" class="edit-input" />
      </div>

      <!-- Conteo de frutas: para cooperativas cuando el modo requiere conteo -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COOPERATIVE' &&
          editModeObj &&
          editModeObj.requires_fruit_count &&
          !isEditDefectCategory
        "
      >
        <label class="edit-label">Número de frutas:</label>
        <input
          type="number"
          v-model="editFruitCount"
          min="1"
          class="edit-input"
        />
      </div>

      <!-- KG Totales: para cooperativas cuando el modo requiere conteo -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COOPERATIVE' &&
          editModeObj &&
          editModeObj.requires_fruit_count &&
          !isEditDefectCategory
        "
      >
        <label class="edit-label">KG Totales:</label>
        <input
          type="number"
          v-model="editUnitsWeightKg"
          min="0"
          step="0.001"
          class="edit-input"
        />
      </div>

      <!-- Fecha de creación -->
      <div class="edit-section">
        <label class="edit-label">Fecha de creación:</label>
        <input
          type="datetime-local"
          v-model="editCreatedOn"
          class="edit-input"
        />
      </div>

      <div class="icon-editar">
        <div class="icon-wrapper">
          <img
            :src="cancelIcon"
            alt="Cancelar"
            class="icon cancel"
            @click="closeEditDialog"
          />
          <p class="icon-label">Cancelar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="okeyIcon"
            alt="Confirmar"
            class="icon okey"
            @click="confirmEdit"
          />
          <p class="icon-label">Confirmar</p>
        </div>
      </div>
    </dialog>

    <!-- Confirmación edición -->
    <dialog
      ref="editConfirmDialog"
      class="confirm-dialog"
      :class="{ error: editIsError }"
    >
      <img
        :src="editIsError ? errorIcon : exitoIcon"
        class="confirm-icon"
        :alt="editIsError ? 'Error' : 'Éxito'"
      />
      <p class="dialog-message">{{ editConfirmMessage }}</p>
      <div class="confirm-buttons">
        <button @click="closeEditConfirmDialog">Aceptar</button>
      </div>
    </dialog>

    <!-- Confirmación eliminación -->
    <dialog
      ref="deleteConfirmDialog"
      class="confirm-dialog"
      :class="{ error: deleteIsError }"
    >
      <img
        :src="deleteIsError ? errorIcon : exitoIcon"
        class="confirm-icon"
        :alt="deleteIsError ? 'Error' : 'Éxito'"
      />
      <p class="dialog-message">{{ deleteConfirmMessage }}</p>
      <div class="confirm-buttons">
        <button @click="closeDeleteConfirmDialog">Aceptar</button>
      </div>
    </dialog>
  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import Vuetable from "vuetable-2/src/components/Vuetable";
import * as XLSX from "xlsx";

import cancelIcon from "@/assets/icon/cancel.png";
import editIcon from "@/assets/icon/edit.png";
import deleteIcon from "@/assets/icon/eliminate.png";
import errorIcon from "@/assets/icon/error.png";
import exitoIcon from "@/assets/icon/exito.png";
import okeyIcon from "@/assets/icon/okey.png";

const createEmptyControlFilters = () => ({
  farmlandId: "",
  zoneId: "",
  productionMode: "",
  category: "",
  formatId: "",
  createdBy: "",
  lastModifiedBy: "",
});

const DEFAULT_OTROS_DEFECTOS_SUBCATEGORIES = [
  "Blanda",
  "Sangrante",
  "Rota",
  "Deforme",
  "Pétalo pegado",
  "Cicatriz",
];

export default {
  name: "ListProductionControl",
  components: {
    Vuetable,
    DateRangePicker,
  },
  data() {
    return {
      cancelIcon,
      editIcon,
      deleteIcon,
      okeyIcon,
      errorIcon,
      exitoIcon,
      fields: [
        { name: "farmland.name", title: "Finca" },
        { name: "zone.code", title: "Zona", sortField: "zone.code" },
        { name: "production_mode_display", title: "Modo" },
        { name: "category", title: "Categoría" },
        { name: "berries_defects_display", title: "Defectos" },
        { name: "format_display", title: "Formato" },
        { name: "units_display", title: "Nº Cajas" },
        { name: "fruit_count_display", title: "Nº Frutas" },
        { name: "total_display", title: "KG Totales" },
        {
          name: "created_on_display",
          title: "Fecha/Hora",
          sortField: "created_on_display",
        },
        { name: "modified_on_display", title: "Últ. edición" },
        { name: "last_modified_by_display", title: "Últ. modificación por" },
        { name: "id", title: "", dataClass: "hidden-id-field" },
      ],
      perPage: 20,
      sortOrder: [],
      tablePagination: null,
      data: [],
      exportPageSize: 200,
      selectedItem: null,
      editFarmType: null,
      editFormatId: null,
      editUnits: 0,
      editProductionMode: null,
      editCategory: null,
      editSubcategories: [],
      editFruitCount: 0,
      formats: [],
      availableModes: [],
      availableCategories: [],
      editConfirmMessage: "",
      deleteConfirmMessage: "",
      editIsError: false,
      deleteIsError: false,
      pendingRedirect: null,
      editUnitsWeightKg: 0,
      isStaff: false,
      availableFarmlands: [],
      availableZones: [],
      allZones: [],
      filterCategories: [],
      appliedFilters: createEmptyControlFilters(),
      filterDraft: createEmptyControlFilters(),
      editFarmlandId: null,
      editZoneId: null,
      editCreatedOn: null,
      isInitializingEditDialog: false,
      dateRange: {
        startDate: new Date(Date.now() - 7 * 24 * 3600 * 1000),
        endDate: new Date(),
      },
      locale: {
        direction: "ltr",
        format: "dd/mm/yyyy",
        separator: " - ",
        applyLabel: "APLICAR",
        cancelLabel: "Cancelar",
        weekLabel: "S",
        customRangeLabel: "Personalizado",
        daysOfWeek: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
        monthNames: [
          "Enero",
          "Febrero",
          "Marzo",
          "Abril",
          "Mayo",
          "Junio",
          "Julio",
          "Agosto",
          "Septiembre",
          "Octubre",
          "Noviembre",
          "Diciembre",
        ],
        firstDay: 1,
      },
    };
  },

  computed: {
    sortedFilterFarmlands() {
      return [...this.availableFarmlands].sort((a, b) => {
        const nameCompare = String(a.name || "").localeCompare(
          String(b.name || ""),
          "es",
          {
            sensitivity: "base",
            numeric: true,
          },
        );
        if (nameCompare !== 0) return nameCompare;
        return String(a.code || "").localeCompare(String(b.code || ""), "es", {
          sensitivity: "base",
          numeric: true,
        });
      });
    },
    sortedFilterModes() {
      return [...this.availableModes].sort((a, b) => {
        const nameCompare = String(a.name || "").localeCompare(
          String(b.name || ""),
          "es",
          {
            sensitivity: "base",
            numeric: true,
          },
        );
        if (nameCompare !== 0) return nameCompare;
        return String(a.code || "").localeCompare(String(b.code || ""), "es", {
          sensitivity: "base",
          numeric: true,
        });
      });
    },
    sortedFilterCategories() {
      return [...this.filterCategories].sort((a, b) => {
        const nameCompare = String(a.name || "").localeCompare(
          String(b.name || ""),
          "es",
          {
            sensitivity: "base",
            numeric: true,
          },
        );
        if (nameCompare !== 0) return nameCompare;
        return String(a.code || "").localeCompare(String(b.code || ""), "es", {
          sensitivity: "base",
          numeric: true,
        });
      });
    },
    editModeObj() {
      return (
        this.availableModes.find((m) => m.code === this.editProductionMode) ||
        null
      );
    },
    selectedEditCategoryObj() {
      if (!this.editCategory) return null;
      return (
        this.availableCategories.find(
          (category) => category.code === this.editCategory,
        ) || null
      );
    },
    editSubcategoryOptions() {
      const raw = this.selectedEditCategoryObj?.subcategories;
      if (Array.isArray(raw)) {
        const cleaned = raw
          .map((value) => String(value || "").trim())
          .filter((value) => value);
        if (cleaned.length > 0) return cleaned;
      }

      if (this.editCategory === "OTROS_DEFECTOS") {
        return DEFAULT_OTROS_DEFECTOS_SUBCATEGORIES;
      }

      return [];
    },
    isEditDefectCategory() {
      return (
        this.editFarmType === "COOPERATIVE" &&
        this.editSubcategoryOptions.length > 0
      );
    },
    canChangeFarmland() {
      return this.isStaff || this.availableFarmlands.length > 1;
    },
    ranges() {
      const now = new Date();
      return {
        "Última hora": [new Date(now - 3600 * 1000), new Date(now)],
        "Últimas 6 horas": [new Date(now - 6 * 3600 * 1000), new Date(now)],
        "Últimas 12 horas": [new Date(now - 12 * 3600 * 1000), new Date(now)],
        "24 horas": [new Date(now - 24 * 3600 * 1000), new Date(now)],
        "3 días": [new Date(now - 3 * 24 * 3600 * 1000), new Date(now)],
        Semana: [new Date(now - 7 * 24 * 3600 * 1000), new Date(now)],
      };
    },
    dateRangeLabel() {
      const fmt = (d) => {
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getDate())}/${pad(
          d.getMonth() + 1,
        )}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      return `${fmt(new Date(this.dateRange.startDate))} – ${fmt(
        new Date(this.dateRange.endDate),
      )}`;
    },
    filteredZoneOptions() {
      const farmlandId = Number(this.filterDraft.farmlandId);
      const zonesToSort =
        !Number.isFinite(farmlandId) || farmlandId <= 0
          ? this.allZones
          : this.allZones.filter(
              (zone) => Number(zone.farmland) === farmlandId,
            );

      return [...zonesToSort].sort((a, b) => {
        const nameCompare = String(a.name || "").localeCompare(
          String(b.name || ""),
          "es",
          {
            sensitivity: "base",
            numeric: true,
          },
        );

        if (nameCompare !== 0) return nameCompare;

        return String(a.code || "").localeCompare(String(b.code || ""), "es", {
          sensitivity: "base",
          numeric: true,
        });
      });
    },
    filteredFormatOptions() {
      const farmlandId = Number(this.filterDraft.farmlandId);
      if (Number.isFinite(farmlandId) && farmlandId > 0) {
        const selectedFarmland = this.availableFarmlands.find(
          (farmland) => Number(farmland.id) === farmlandId,
        );
        return [...(selectedFarmland?.formats || [])].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "es", {
            sensitivity: "base",
            numeric: true,
          }),
        );
      }

      const uniqueFormats = new Map();
      this.availableFarmlands.forEach((farmland) => {
        (farmland.formats || []).forEach((format) => {
          if (!uniqueFormats.has(format.id)) {
            uniqueFormats.set(format.id, format);
          }
        });
      });

      return Array.from(uniqueFormats.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "es", {
          sensitivity: "base",
        }),
      );
    },
    hasActiveFilters() {
      return Object.values(this.appliedFilters).some(
        (value) => String(value || "").trim() !== "",
      );
    },
    activeFilterCount() {
      return Object.values(this.appliedFilters).filter(
        (value) => String(value || "").trim() !== "",
      ).length;
    },
    totalControlRecords() {
      const total = Number(this.tablePagination?.total);
      if (Number.isFinite(total) && total >= 0) {
        return total;
      }
      return this.data.length;
    },
  },

  watch: {
    async editFarmlandId(newId, oldId) {
      if (!newId || newId === oldId) return;
      if (this.isInitializingEditDialog) return;

      this.editZoneId = null;
      await this.loadZonesForFarmland(newId);
      // Actualizar tipo de finca al cambiar de finca
      const fl = this.availableFarmlands.find(
        (f) => f.id === newId || f.id === parseInt(newId),
      );
      if (fl) this.editFarmType = fl.farm_type;
    },
    async editProductionMode(newMode, oldMode) {
      if (newMode === oldMode) return;

      await this.loadCategoriesForMode(newMode);

      if (this.editCategory) {
        const categoryExists = this.availableCategories.some(
          (cat) => cat.code === this.editCategory,
        );
        if (!categoryExists) {
          this.editCategory = null;
        }
      }

      if (this.editModeObj?.requires_format) {
        if (
          this.formats.length === 0 &&
          this.selectedItem &&
          this.selectedItem.farmland_id
        ) {
          this.loadFormatsForFarmland(this.selectedItem.farmland_id);
        }
      } else {
        this.editFormatId = null;
      }

      this.syncEditSubcategoriesWithOptions();
    },
    editCategory(newCategory, oldCategory) {
      if (newCategory === oldCategory) return;
      this.syncEditSubcategoriesWithOptions();
    },
    "filterDraft.farmlandId"() {
      this.ensureFilterDraftIsValid();
    },
  },

  mounted() {
    // Leer is_staff del payload del token JWT (funciona con sesiones ya iniciadas)
    try {
      const token = localStorage.getItem("token");
      const payload = token ? JSON.parse(atob(token.split(".")[1])) : {};
      this.isStaff = payload.is_staff === true;
    } catch {
      this.isStaff = false;
    }
    this.$nextTick(() => {
      this.refreshProductionTable();
    });
    this.loadFarmlands();
  },

  methods: {
    normalizeFilterPayload(filters) {
      const normalizeId = (value) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : "";
      };

      return {
        farmlandId: normalizeId(filters.farmlandId),
        zoneId: normalizeId(filters.zoneId),
        productionMode: (filters.productionMode || "").trim(),
        category: (filters.category || "").trim(),
        formatId: normalizeId(filters.formatId),
        createdBy: (filters.createdBy || "").trim(),
        lastModifiedBy: (filters.lastModifiedBy || "").trim(),
      };
    },

    ensureFilterDraftIsValid() {
      if (
        this.filterDraft.zoneId &&
        !this.filteredZoneOptions.some(
          (zone) => String(zone.id) === String(this.filterDraft.zoneId),
        )
      ) {
        this.filterDraft.zoneId = "";
      }

      if (
        this.filterDraft.formatId &&
        !this.filteredFormatOptions.some(
          (format) => String(format.id) === String(this.filterDraft.formatId),
        )
      ) {
        this.filterDraft.formatId = "";
      }
    },

    async ensureFilterMetadataLoaded() {
      const pendingRequests = [];
      if (!this.availableFarmlands.length) {
        pendingRequests.push(this.loadFarmlands());
      }
      if (!this.availableModes.length) {
        pendingRequests.push(this.loadModes());
      }
      if (!this.filterCategories.length) {
        pendingRequests.push(this.loadAllCategories());
      }
      if (!this.allZones.length) {
        pendingRequests.push(this.loadAllZones());
      }

      if (pendingRequests.length) {
        await Promise.all(pendingRequests);
      }
    },

    async openFilterDialog() {
      try {
        await this.ensureFilterMetadataLoaded();
        this.filterDraft = { ...this.appliedFilters };
        this.ensureFilterDraftIsValid();
        this.$refs.filterDialog.showModal();
      } catch (error) {
        this.showEditError("No se pudieron cargar las opciones de filtros.");
      }
    },

    closeFilterDialog() {
      if (this.$refs.filterDialog && this.$refs.filterDialog.open) {
        this.$refs.filterDialog.close();
      }
    },

    applyFilters() {
      this.ensureFilterDraftIsValid();
      this.appliedFilters = this.normalizeFilterPayload(this.filterDraft);
      this.closeFilterDialog();
      this.goToPage(1);
    },

    clearFilters() {
      this.filterDraft = createEmptyControlFilters();
      this.appliedFilters = createEmptyControlFilters();
      this.closeFilterDialog();
      this.goToPage(1);
    },

    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const pad = (num) => String(num).padStart(2, "0");
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic",
      ];
      return `${pad(date.getDate())} ${
        months[date.getMonth()]
      } ${date.getFullYear()}, ${pad(date.getHours())}:${pad(
        date.getMinutes(),
      )}`;
    },

    onPaginationData(paginationData) {
      this.tablePagination = paginationData || null;
    },
    onChangePage(page) {
      this.goToPage(page);
    },
    onSortOrderChanged(sortOrder) {
      this.sortOrder = Array.isArray(sortOrder) ? sortOrder : [];
      this.goToPage(1);
    },
    goToPage(page) {
      if (!this.$refs.vuetable) return;
      const parsedPage = Number(page);
      if (!Number.isFinite(parsedPage)) return;

      const currentPage = Number(this.tablePagination?.current_page || 1);
      const lastPage = Number(this.tablePagination?.last_page || parsedPage);
      let targetPage = Math.max(1, Math.trunc(parsedPage));

      if (Number.isFinite(lastPage) && lastPage > 0) {
        targetPage = Math.min(targetPage, lastPage);
      }

      if (targetPage === currentPage) {
        this.refreshProductionTable();
        return;
      }

      this.$refs.vuetable.changePage(targetPage);
    },
    getSortedParam(sortOrder = this.sortOrder) {
      if (!Array.isArray(sortOrder) || !sortOrder.length) return "";

      const currentSort = sortOrder[0] || {};
      const field = String(currentSort.field || "");
      const direction = String(currentSort.direction || "").toLowerCase();

      if (field === "zone.code") {
        return direction === "desc" ? "-zone" : "zone";
      }

      if (field === "created_on_display") {
        return direction === "desc" ? "-created_on" : "created_on";
      }

      return "";
    },
    buildProductionControlEndpoint(
      page = 1,
      pageSize = this.perPage,
      sorted = "",
    ) {
      // toISOString() gives UTC; convert to local time so Django (USE_TZ=False) matches stored naive datetimes
      const toLocalISO = (d) => {
        const date = new Date(d);
        const offset = date.getTimezoneOffset() * 60000;
        return new Date(date - offset).toISOString().slice(0, 23);
      };

      const from = toLocalISO(this.dateRange.startDate);
      const to = toLocalISO(this.dateRange.endDate);

      const params = new URLSearchParams({
        from_date: from,
        to_date: to,
        page: String(page),
        page_size: String(pageSize),
      });

      if (this.appliedFilters.farmlandId) {
        params.set("farmland_id", this.appliedFilters.farmlandId);
      }
      if (this.appliedFilters.zoneId) {
        params.set("zone_id", this.appliedFilters.zoneId);
      }
      if (this.appliedFilters.productionMode) {
        params.set("production_mode", this.appliedFilters.productionMode);
      }
      if (this.appliedFilters.category) {
        params.set("category", this.appliedFilters.category);
      }
      if (this.appliedFilters.formatId) {
        params.set("format_id", this.appliedFilters.formatId);
      }
      if (this.appliedFilters.createdBy) {
        params.set("created_by", this.appliedFilters.createdBy);
      }
      if (this.appliedFilters.lastModifiedBy) {
        params.set("last_modified_by", this.appliedFilters.lastModifiedBy);
      }
      if (sorted) {
        params.set("sorted", sorted);
      }

      return `/production/control/?${params.toString()}`;
    },

    buildTablePagination(responseData, pageSize, currentPage) {
      const pagination = responseData.pagination || {};
      const total = pagination.total ?? responseData.count ?? 0;
      return {
        total,
        per_page: pagination.per_page ?? pageSize,
        current_page: pagination.current_page ?? currentPage,
        last_page:
          pagination.last_page ??
          (Math.ceil(total / (pageSize || this.perPage)) || 0),
        next_page_url: pagination.next_page_url ?? responseData.next ?? "",
        prev_page_url: pagination.prev_page_url ?? responseData.previous ?? "",
        from: pagination.from ?? 0,
        to: pagination.to ?? 0,
      };
    },

    normalizeProductionRows(items) {
      return items.map((item) => {
        const normalized = { ...item };
        normalized.created_on_display = this.formatDateTime(
          normalized.created_on,
        );

        // Format zone: show "-" if no zone
        if (!normalized.zone) {
          normalized.zone = { name: "-", code: "-" };
        } else {
          if (!normalized.zone.name) {
            normalized.zone.name = "-";
          }
          if (!normalized.zone.code) {
            normalized.zone.code = "-";
          }
        }

        if (normalized.production_mode === "WEIGHT") {
          normalized.production_mode_display = "Peso";
        } else if (normalized.production_mode === "COUNT") {
          normalized.production_mode_display = "Conteo";
        } else if (normalized.production_mode === "LOTE") {
          normalized.production_mode_display = "Lote";
        } else {
          normalized.production_mode_display = "-";
        }

        if (!normalized.category) {
          normalized.category = "-";
        }

        normalized.berries_defects_display =
          normalized.berries_defects_display || "-";

        if (
          normalized.production_mode === "COUNT" ||
          normalized.production_mode === "LOTE"
        ) {
          normalized.format_display = "-";
          normalized.units_display = "-";
          if (normalized.units_weight) {
            normalized.total = normalized.units_weight / 1000;
            normalized.total_display =
              Math.round(normalized.total * 1000) / 1000;
          } else {
            normalized.total = 0;
            normalized.total_display = "-";
          }
          normalized.fruit_count_display = normalized.fruit_count || "-";
        } else {
          normalized.format_display = normalized.format?.name || "-";
          normalized.units_display = normalized.units || 0;
          if (normalized.format && normalized.units) {
            normalized.total =
              (normalized.units * normalized.format.weight) / 1000;
            normalized.total_display = normalized.total;
          } else {
            normalized.total = 0;
            normalized.total_display = 0;
          }
          normalized.fruit_count_display = "-";
        }

        normalized.modified_on_display = normalized.modified_on
          ? this.formatDateTime(normalized.modified_on)
          : "-";
        normalized.last_modified_by_display =
          normalized.last_modified_by || "-";

        return normalized;
      });
    },

    async dataManager(sortOrder, pagination) {
      const currentPage = pagination?.current_page || 1;
      const pageSize = pagination?.per_page || this.perPage;
      const effectiveSortOrder =
        Array.isArray(sortOrder) && sortOrder.length
          ? sortOrder
          : this.sortOrder;
      const sortedParam = this.getSortedParam(effectiveSortOrder);

      try {
        const response = await this.$http.get(
          this.$hostname +
            this.buildProductionControlEndpoint(
              currentPage,
              pageSize,
              sortedParam,
            ),
        );
        const rows = this.normalizeProductionRows(response.data.results || []);
        const tablePagination = this.buildTablePagination(
          response.data,
          pageSize,
          currentPage,
        );
        this.data = rows;
        this.tablePagination = tablePagination;
        return {
          pagination: tablePagination,
          data: rows,
        };
      } catch (error) {
        const backendMsg =
          error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : "Error al obtener el control de producción";

        if (error.response && error.response.status === 403) {
          this.pendingRedirect = "/production";
        }

        this.data = [];
        const errorPagination = {
          total: 0,
          per_page: pageSize,
          current_page: 1,
          last_page: 0,
          next_page_url: "",
          prev_page_url: "",
          from: 0,
          to: 0,
        };
        this.tablePagination = errorPagination;
        this.showEditError(backendMsg);

        return {
          pagination: errorPagination,
          data: [],
        };
      }
    },

    refreshProductionTable() {
      if (!this.$refs.vuetable) return;
      return this.$refs.vuetable.reload();
    },

    async fetchAllProductionRows() {
      const rows = [];
      let currentPage = 1;
      let hasMorePages = true;
      const sortedParam = this.getSortedParam(this.sortOrder);

      while (hasMorePages) {
        const response = await this.$http.get(
          this.$hostname +
            this.buildProductionControlEndpoint(
              currentPage,
              this.exportPageSize,
              sortedParam,
            ),
        );

        rows.push(...this.normalizeProductionRows(response.data.results || []));

        const pagination = this.buildTablePagination(
          response.data,
          this.exportPageSize,
          currentPage,
        );

        hasMorePages =
          pagination.last_page !== 0 && currentPage < pagination.last_page;

        if (hasMorePages) {
          currentPage += 1;
        }
      }

      return rows;
    },

    onDateRangeApply(values) {
      this.dateRange = values;
      this.goToPage(1);
    },

    onRowClick(data) {
      if (data.data) {
        this.selectedItem = { ...data.data };
      } else {
        this.selectedItem = { ...data };
      }

      let farmlandId = null;
      if (this.selectedItem.farmland_id) {
        farmlandId = this.selectedItem.farmland_id;
      } else if (this.selectedItem.farmland && this.selectedItem.farmland.id) {
        farmlandId = this.selectedItem.farmland.id;
      }

      if (!farmlandId) {
        this.showEditError("Error: Datos de finca no disponibles");
        return;
      }

      this.selectedItem.farmland_id = farmlandId;
      this.$refs.optionsDialog.showModal();
    },

    closeDialog() {
      this.selectedItem = null;
      this.$refs.optionsDialog.close();
    },

    async openEditDialog() {
      if (!this.selectedItem || !this.selectedItem.farmland_id) {
        this.showEditError("Finca no disponible");
        return;
      }

      this.isInitializingEditDialog = true;

      try {
        this.editFarmType =
          this.selectedItem.farmland?.farm_type || "COMMERCIAL";
        this.editProductionMode = this.selectedItem.production_mode || null;
        this.editCategory =
          this.selectedItem.category === "-"
            ? null
            : this.selectedItem.category;
        this.editSubcategories = this.normalizeDefectSelection(
          this.selectedItem.berries_defects,
        );
        this.editFruitCount = this.selectedItem.fruit_count || 0;
        this.editUnits = this.selectedItem.units || 0;
        this.editCreatedOn = this.toDatetimeLocalString(
          this.selectedItem.created_on,
        );
        this.editUnitsWeightKg = this.selectedItem.units_weight
          ? Math.round((this.selectedItem.units_weight / 1000) * 1000) / 1000
          : 0;

        const targetFarmlandId =
          this.selectedItem.farmland?.id ||
          this.selectedItem.farmland_id ||
          null;
        const targetZoneId = this.selectedItem.zone?.id || null;

        // Finca y zona actuales
        this.editFarmlandId = targetFarmlandId;

        // Cargar zonas de la finca actual
        if (targetFarmlandId) {
          await this.loadZonesForFarmland(targetFarmlandId);
        } else {
          this.availableZones = [];
        }

        this.editZoneId =
          targetZoneId &&
          this.availableZones.some(
            (zone) => String(zone.id) === String(targetZoneId),
          )
            ? targetZoneId
            : null;

        // Load modes and categories from API
        await this.loadModes();
        if (this.editProductionMode) {
          await this.loadCategoriesForMode(this.editProductionMode);
        }
        this.syncEditSubcategoriesWithOptions();

        if (
          this.editFarmType === "COMMERCIAL" ||
          this.editModeObj?.requires_format
        ) {
          await this.loadFormatsForFarmland(this.selectedItem.farmland_id);

          if (this.formats.length === 0) {
            this.showEditError("No hay formatos disponibles para esta finca");
            return;
          }

          this.editFormatId =
            this.selectedItem.format_id ||
            (this.selectedItem.format && this.selectedItem.format.id);
        }

        this.$refs.editDialog.showModal();
      } catch (error) {
        this.showEditError("Error al cargar los datos para edición");
      } finally {
        this.isInitializingEditDialog = false;
      }
    },

    closeEditDialog() {
      this.$refs.editDialog.close();
      this.closeDialog();
      this.selectedItem = null;
    },

    closeEditConfirmDialog() {
      this.$refs.editConfirmDialog.close();
      if (this.pendingRedirect) {
        const path = this.pendingRedirect;
        this.pendingRedirect = null;
        this.$router.push(path);
      }
    },

    closeDeleteConfirmDialog() {
      this.$refs.deleteConfirmDialog.close();
    },

    toDatetimeLocalString(isoStr) {
      if (!isoStr) return "";
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    async loadFormatsForFarmland(farmlandId) {
      try {
        const response = await this.$http.get(
          `${this.$hostname}/farmlands/${farmlandId}/formats/`,
        );
        this.formats = response.data;
      } catch (err) {
        this.showEditError(
          "No se pudieron cargar los formatos para esta finca",
        );
      }
    },

    async loadModes() {
      try {
        const response = await this.$http.get(`${this.$hostname}/modes/`);
        this.availableModes = response.data;
      } catch (e) {
        console.error("Error al cargar modos:", e);
        this.availableModes = [];
      }
    },

    async loadCategoriesForMode(modeCode) {
      if (!modeCode) {
        this.availableCategories = [];
        return;
      }
      try {
        const response = await this.$http.get(
          `${this.$hostname}/categories/?mode=${modeCode}`,
        );
        this.availableCategories = response.data;
      } catch (e) {
        console.error("Error al cargar categorías:", e);
        this.availableCategories = [];
      }
    },

    async loadAllCategories() {
      try {
        const response = await this.$http.get(`${this.$hostname}/categories/`);
        this.filterCategories = response.data;
      } catch (e) {
        console.error("Error al cargar categorías para filtros:", e);
        this.filterCategories = [];
      }
    },

    async loadFarmlands() {
      try {
        const response = await this.$http.get(`${this.$hostname}/farmlands/`);
        this.availableFarmlands = response.data;
      } catch (e) {
        console.error("Error al cargar fincas:", e);
        this.availableFarmlands = [];
      }
    },

    async loadAllZones() {
      try {
        const response = await this.$http.get(`${this.$hostname}/zones/`);
        this.allZones = response.data;
      } catch (e) {
        console.error("Error al cargar zonas para filtros:", e);
        this.allZones = [];
      }
    },

    async loadZonesForFarmland(farmlandId) {
      if (!farmlandId) {
        this.availableZones = [];
        return;
      }
      try {
        const response = await this.$http.get(
          `${this.$hostname}/farmlands/${farmlandId}/zones/`,
        );
        this.availableZones = response.data;
      } catch (e) {
        console.error("Error al cargar zonas:", e);
        this.availableZones = [];
      }
    },

    showEditSuccess(message) {
      this.editIsError = false;
      this.editConfirmMessage = message;
      this.$refs.editConfirmDialog.showModal();
    },

    showEditError(message) {
      this.editIsError = true;
      this.editConfirmMessage = message;
      this.$refs.editConfirmDialog.showModal();
    },

    showDeleteSuccess(message) {
      this.deleteIsError = false;
      this.deleteConfirmMessage = message;
      this.$refs.deleteConfirmDialog.showModal();
    },

    normalizeDefectKey(value) {
      return String(value || "")
        .trim()
        .toLocaleLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ");
    },

    normalizeDefectSelection(rawValue) {
      if (!rawValue) return [];

      if (Array.isArray(rawValue)) {
        return rawValue
          .map((value) => String(value || "").trim())
          .filter((value) => value);
      }

      if (typeof rawValue === "object") {
        return Object.entries(rawValue)
          .filter(([, selected]) => Boolean(selected))
          .map(([key]) => String(key || "").trim())
          .filter((value) => value);
      }

      if (typeof rawValue === "string") {
        return rawValue
          .split(",")
          .map((value) => value.trim())
          .filter((value) => value);
      }

      return [];
    },

    syncEditSubcategoriesWithOptions() {
      const options = this.editSubcategoryOptions;
      if (!options.length) {
        this.editSubcategories = [];
        return;
      }

      const optionMap = new Map(
        options.map((option) => [this.normalizeDefectKey(option), option]),
      );

      const normalized = [];
      this.normalizeDefectSelection(this.editSubcategories).forEach((value) => {
        const canonical = optionMap.get(this.normalizeDefectKey(value));
        if (canonical && !normalized.includes(canonical)) {
          normalized.push(canonical);
        }
      });

      this.editSubcategories = normalized;
    },

    async confirmEdit() {
      const requestData = { production_id: this.selectedItem.id };

      // Enviar finca si ha cambiado
      const currentFarmlandId =
        this.selectedItem.farmland?.id || this.selectedItem.farmland_id;
      if (
        this.editFarmlandId &&
        parseInt(this.editFarmlandId) !== parseInt(currentFarmlandId)
      ) {
        requestData.farmland_id = this.editFarmlandId;
      }

      // Enviar zona siempre (permite quitar zona con null/0)
      requestData.zone_id = this.editZoneId || 0;

      // Enviar fecha de creación si ha cambiado
      if (this.editCreatedOn) {
        requestData.created_on = this.editCreatedOn;
      }

      if (this.editFarmType === "COMMERCIAL") {
        if (!this.editFormatId || !this.editUnits || this.editUnits <= 0) {
          this.showEditError("Por favor, complete formato y cantidad de cajas");
          return;
        }
        requestData.format_id = this.editFormatId;
        requestData.units = this.editUnits;
      } else if (this.editFarmType === "COOPERATIVE") {
        if (!this.editProductionMode || !this.editCategory) {
          this.showEditError("Por favor, seleccione modo y categoría");
          return;
        }
        requestData.production_mode = this.editProductionMode;
        requestData.category = this.editCategory;

        if (this.isEditDefectCategory) {
          this.syncEditSubcategoriesWithOptions();
          if (!this.editSubcategories.length) {
            this.showEditError(
              "Por favor, seleccione al menos una subcategoría",
            );
            return;
          }
          requestData.berries_defects = this.editSubcategories;
        } else if (this.editModeObj?.requires_format) {
          if (!this.editFormatId || !this.editUnits || this.editUnits <= 0) {
            this.showEditError(
              "Por favor, complete todos los campos válidos para modo por peso",
            );
            return;
          }
          requestData.format_id = this.editFormatId;
          requestData.units = this.editUnits;
        } else if (this.editModeObj?.requires_fruit_count) {
          if (!this.editFruitCount || this.editFruitCount < 1) {
            this.showEditError("Por favor, complete el número de frutas");
            return;
          }
          requestData.fruit_count = this.editFruitCount;
          requestData.units_weight = Math.round(
            (this.editUnitsWeightKg || 0) * 1000,
          );
        }
      }

      try {
        const response = await this.$http.patch(
          `${this.$hostname}/production/`,
          requestData,
        );

        if (response.status === 200) {
          this.closeEditDialog();
          this.closeDialog();
          this.showEditSuccess("Registro actualizado correctamente");
          await this.refreshProductionTable();
        } else {
          this.showEditError(
            response.data.error || "Error desconocido al actualizar",
          );
        }
      } catch (error) {
        let errorMsg = "Error al editar el registro";
        if (error.response && error.response.data) {
          errorMsg =
            error.response.data.error ||
            error.response.data.message ||
            `Error ${error.response.status}`;
        }
        this.showEditError(errorMsg);
      }
    },

    async confirmDelete() {
      if (!this.selectedItem || !this.selectedItem.id) {
        this.showEditError("No hay registro seleccionado o ID inválido");
        return;
      }

      try {
        const response = await this.$http.delete(
          `${this.$hostname}/production/${this.selectedItem.id}/`,
          { headers: { "Content-Type": "application/json" } },
        );

        if (response.status === 204) {
          this.closeDialog();
          this.showDeleteSuccess("Registro eliminado correctamente");
          await this.refreshProductionTable();
        } else {
          this.showEditError("Error al eliminar el registro");
        }
      } catch (error) {
        let errorMsg = "Error al eliminar el registro";
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          errorMsg = error.response.data.error;
        }
        this.showEditError(errorMsg);
      }
    },

    async exportToCSV() {
      let rows;

      try {
        rows = await this.fetchAllProductionRows();
      } catch (error) {
        this.showEditError("Error al exportar el control de producción");
        return;
      }

      const headers = [
        "Finca",
        "Zona",
        "Modo",
        "Categoría",
        "Defectos",
        "Formato",
        "Nº Cajas",
        "Nº Frutas",
        "KG Totales",
        "Fecha/Hora",
        "Últ. modificación por",
      ];

      const csvRows = rows.map((item) => [
        item.farmland?.name || "",
        item.zone?.code || "-",
        item.production_mode_display || "-",
        item.category || "-",
        item.berries_defects_display || "-",
        item.format_display || "-",
        item.units_display !== undefined ? item.units_display : "-",
        item.fruit_count_display || "-",
        item.total_display !== undefined ? item.total_display : "-",
        item.created_on_display || "",
        item.last_modified_by_display || "-",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `control_produccion_${new Date().getTime()}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async exportToExcel() {
      let rows;

      try {
        rows = await this.fetchAllProductionRows();
      } catch (error) {
        this.showEditError("Error al exportar el control de producción");
        return;
      }

      const headers = [
        "Finca",
        "Zona",
        "Modo",
        "Categoría",
        "Defectos",
        "Formato",
        "Nº Cajas",
        "Nº Frutas",
        "KG Totales",
        "Fecha/Hora",
        "Últ. modificación por",
      ];

      const excelRows = rows.map((item) => [
        item.farmland?.name || "",
        item.zone?.code || "-",
        item.production_mode_display || "-",
        item.category || "-",
        item.berries_defects_display || "-",
        item.format_display || "-",
        item.units_display !== undefined ? item.units_display : "-",
        item.fruit_count_display || "-",
        item.total_display !== undefined ? item.total_display : "-",
        item.created_on_display || "",
        item.last_modified_by_display || "-",
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Control produccion");
      XLSX.writeFile(
        workbook,
        `control_produccion_${new Date().getTime()}.xlsx`,
      );
    },
  },
};
</script>

<style>
.hidden-id-field {
  display: none;
}

/* =====================================================
   DATE RANGE PICKER – trigger button
   ===================================================== */
.date-range-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px 6px;
}

.filter-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border: 1.5px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
  color: #344054;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s, color 0.18s;
}

.filter-open-btn:hover {
  border-color: #107631;
  color: #107631;
  box-shadow: 0 0 0 3px rgba(16, 118, 49, 0.1);
}

.filter-open-btn.active {
  border-color: #107631;
  color: #107631;
}

.filter-open-btn-badge {
  min-width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #107631;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  padding: 0 6px;
  text-align: center;
}

.subcategory-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.subcategory-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #344054;
}

.filter-dialog {
  width: min(92vw, 760px);
  max-width: 760px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 16px;
}

.filter-grid .edit-input {
  width: 100%;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.filter-actions button {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.filter-clear-btn,
.filter-cancel-btn {
  background: #ffffff;
  color: #344054;
}

.filter-apply-btn {
  background: #107631;
  border-color: #107631 !important;
  color: #ffffff;
}

.date-range-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 18px;
  border: 1.5px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.06);
  transition: border-color 0.18s, box-shadow 0.18s;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.date-range-btn:hover {
  border-color: #107631;
  box-shadow: 0 0 0 3px rgba(16, 118, 49, 0.1);
}

.date-range-label {
  font-weight: 600;
  color: #107631;
}

/* =====================================================
   DATE RANGE PICKER – popup container
   ===================================================== */
.daterangepicker {
  border: 1px solid #e4e7ec !important;
  border-radius: 14px !important;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.14) !important;
  font-family: inherit !important;
  padding: 0 !important;
  overflow: hidden !important;
  /* Flex layout: ranges | cal-left | cal-right on one row, footer below */
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: flex-start !important;
}

.daterangepicker::before,
.daterangepicker::after {
  display: none !important;
}

.daterangepicker .ranges {
  flex: 0 0 auto !important;
  float: none !important;
  background: #f9fafb !important;
  border-right: 1px solid #e4e7ec !important;
  align-self: stretch !important;
}

/* Override the library's "col" class which normally stretches to full width */
.daterangepicker .drp-calendar {
  flex: 0 0 auto !important;
  float: none !important;
  width: auto !important;
  max-width: none !important;
}

.daterangepicker .drp-calendar.left {
  border-left: 1px solid #e4e7ec !important;
  padding: 14px 16px !important;
}

.daterangepicker .drp-calendar.right {
  border-left: 1px solid #e4e7ec !important;
  padding: 14px 16px !important;
}

/* Footer: full-width flex child + right-aligned buttons inside */
.daterangepicker .drp-buttons {
  flex: 0 0 100% !important;
  width: 100% !important;
  box-sizing: border-box !important;
  float: none !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 10px !important;
  border-top: 1px solid #e4e7ec !important;
  padding: 10px 16px !important;
  background: #fff !important;
}

/* Remove default border on calendar table */
.daterangepicker .calendar-table {
  border: none !important;
  padding: 0 !important;
}

.daterangepicker .calendar-table table {
  border-collapse: collapse !important;
}

.daterangepicker .calendar-table th,
.daterangepicker .calendar-table td {
  min-width: 32px !important;
  width: 32px !important;
  height: 28px !important;
  text-align: center !important;
  padding: 0 !important;
  line-height: 28px !important;
  box-sizing: border-box !important;
}

.daterangepicker .ranges ul {
  padding: 8px 0;
  margin: 0;
}

.daterangepicker .ranges ul li {
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  border-radius: 0;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.daterangepicker .ranges ul li:hover {
  background: #ecf7ee;
  color: #107631;
}

.daterangepicker .ranges ul li.active {
  background: linear-gradient(135deg, #107631 0%, #2ca254 100%);
  color: #fff !important;
}

/* =====================================================
   Calendar header – month / year navigation
   ===================================================== */
.daterangepicker .calendar-table th,
.daterangepicker th.month {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #101828 !important;
}

.daterangepicker .prev span,
.daterangepicker .next span {
  border-color: #107631 !important;
}

/* =====================================================
   Day cells
   ===================================================== */
.daterangepicker td.available:hover {
  background: #ecf7ee !important;
  color: #107631 !important;
  border-radius: 6px !important;
}

.daterangepicker td.active,
.daterangepicker td.active:hover {
  background: #107631 !important;
  border-radius: 6px !important;
}

.daterangepicker td.in-range {
  background: #d6f0db !important;
  color: #0e5e28 !important;
}

.daterangepicker td.today {
  font-weight: 700;
  color: #107631 !important;
}

.daterangepicker td.off {
  color: #c0c8d2 !important;
}

/* =====================================================
   Time picker
   ===================================================== */
.daterangepicker .calendar-time {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
  padding: 6px 0 2px !important;
}

.daterangepicker .calendar-time select {
  border: 1.5px solid #d0d5dd;
  border-radius: 6px;
  padding: 2px 4px;
  font-size: 12px;
  color: #344054;
  background: #fff;
  cursor: pointer;
  width: 52px !important;
  height: 28px !important;
  text-align: center;
}

.daterangepicker .calendar-time select:focus {
  outline: none;
  border-color: #107631;
  box-shadow: 0 0 0 2px rgba(16, 118, 49, 0.12);
}

/* =====================================================
   Footer – date summary + action buttons
   ===================================================== */

.daterangepicker .drp-selected {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: #667085 !important;
  flex: 1 !important;
}

.daterangepicker .cancelBtn {
  padding: 8px 20px !important;
  border-radius: 8px !important;
  border: 1.5px solid #d0d5dd !important;
  background: #fff !important;
  color: #344054 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: background 0.15s, border-color 0.15s !important;
  box-shadow: none !important;
}

.daterangepicker .cancelBtn:hover {
  background: #f2f4f7 !important;
  border-color: #b3b9c4 !important;
}

.daterangepicker .applyBtn {
  padding: 8px 20px !important;
  border-radius: 8px !important;
  background: linear-gradient(135deg, #107631 0%, #2ca254 100%) !important;
  border-color: transparent !important;
  color: #fff !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em !important;
  cursor: pointer !important;
  box-shadow: 0 2px 6px rgba(16, 118, 49, 0.28) !important;
  transition: opacity 0.15s, box-shadow 0.15s !important;
}

.daterangepicker .applyBtn:hover {
  opacity: 0.9 !important;
  box-shadow: 0 4px 10px rgba(16, 118, 49, 0.35) !important;
}

/* =====================================================
   Dropdown selectors (month / year)
   ===================================================== */
.daterangepicker select.monthselect,
.daterangepicker select.yearselect {
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  padding: 2px 4px;
  font-size: 12px;
  color: #344054;
}

/* =====================================================
   Mobile: stack layout
   ===================================================== */
@media (max-width: 640px) {
  .daterangepicker.show-ranges {
    flex-direction: column !important;
  }

  .daterangepicker .ranges {
    border-right: none;
    border-bottom: 1px solid #e4e7ec;
  }

  .date-range-btn {
    font-size: 12px;
    padding: 8px 12px;
  }

  .filter-open-btn {
    font-size: 12px;
    padding: 8px 12px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }
}

.table-container {
  position: relative;
}

.production-control .vuetable-th-zone_code.sortable,
.production-control .vuetable-th-created_on_display.sortable {
  position: sticky;
  top: 0;
  z-index: 3;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.production-control .vuetable-th-zone_code.sortable:hover,
.production-control .vuetable-th-created_on_display.sortable:hover {
  background-color: #ecf7ee;
  color: #0e5e28;
}

.production-control .vuetable-th-zone_code.sorted-asc,
.production-control .vuetable-th-zone_code.sorted-desc,
.production-control .vuetable-th-created_on_display.sorted-asc,
.production-control .vuetable-th-created_on_display.sorted-desc {
  background: linear-gradient(180deg, #e9f7ec 0%, #d8efde 100%);
  color: #0e5e28;
  font-weight: 700;
  box-shadow: inset 0 -2px 0 #107631;
}

.production-control .vuetable-th-zone_code.sorted-asc::after,
.production-control .vuetable-th-zone_code.sorted-desc::after,
.production-control .vuetable-th-created_on_display.sorted-asc::after,
.production-control .vuetable-th-created_on_display.sorted-desc::after {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  line-height: 1;
  color: #107631;
}

.production-control .vuetable-th-zone_code.sorted-asc::after {
  content: "\25B2";
}

.production-control .vuetable-th-zone_code.sorted-desc::after,
.production-control .vuetable-th-created_on_display.sorted-desc::after {
  content: "\25BC";
}

.production-control .vuetable-th-created_on_display.sorted-asc::after {
  content: "\25B2";
}

.records-summary {
  padding: 4px 16px 12px;
}

.records-summary-count {
  display: inline-block;
  font-size: 16px;
  font-weight: 800;
  color: #344054;
  letter-spacing: 0.05em;
  line-height: 1.25;
}

.production-control {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.production-control .table-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.production-control .table-container > .vuetable-body-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
}

.production-control
  .table-container
  > .vuetable-body-wrapper
  > .vuetable-body-wrapper {
  height: calc(100vh - 255px);
  overflow-x: auto;
  overflow-y: auto;
}

.control-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0 16px;
}

.production-control .control-pagination {
  flex-shrink: 0;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.pagination-btn {
  min-width: 34px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.pagination-btn:hover:not(:disabled) {
  border-color: #107631;
  color: #107631;
}

.pagination-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pagination-status {
  min-width: 150px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #475467;
}

.mobile-export-btn {
  display: none !important;
}

.control-export-desktop-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin: 0 16px 10px 16px;
}

.desktop-export-btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #fafafa !important;
  color: #666 !important;
  border: 1px solid #ddd !important;
  box-shadow: none !important;
}

.desktop-export-btn:hover {
  background-color: #f5f5f5 !important;
  border-color: #999 !important;
  color: #333 !important;
  box-shadow: none !important;
  transform: none !important;
}

@media (max-width: 768px) {
  .control-export-mobile-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0 auto 15px auto;
  }

  .mobile-export-btn {
    display: inline-flex !important;
    justify-content: center;
    margin: 0;
    text-align: center;
    width: auto;
    min-width: 140px;
    max-width: 170px;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    background: linear-gradient(135deg, #107631 0%, #98cf9c 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 6px rgba(16, 118, 49, 0.3);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .mobile-export-btn--compact {
    min-width: 138px;
    max-width: 160px;
    padding: 10px 14px;
  }

  .mobile-export-btn:hover {
    background: linear-gradient(135deg, #0d5e28 0%, #7ab87d 100%);
    box-shadow: 0 6px 8px rgba(16, 118, 49, 0.4);
    transform: translateY(-1px);
  }

  .control-export-desktop-actions {
    display: none !important;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .control-pagination {
    flex-wrap: wrap;
    padding: 10px 0 14px;
  }

  .pagination-status {
    width: 100%;
    order: -1;
    margin-bottom: 6px;
  }

  .vuetable-body-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .vuetable {
    font-size: 12px;
    min-width: 700px;
  }

  .vuetable th,
  .vuetable td {
    font-size: 11px;
    padding: 8px 6px;
    white-space: nowrap;
  }
}

/* =====================================================
   DATE PICKER – mobile overrides (≤ 640px)
   ===================================================== */
@media (max-width: 640px) {
  /* Ocultar el segundo mes en móvil */
  .daterangepicker .drp-calendar.right {
    display: none !important;
  }

  /* Popup: columna única (ranges encima, calendario debajo) */
  .daterangepicker {
    flex-wrap: wrap !important;
    width: auto !important;
    max-width: 100vw !important;
  }

  /* Ranges: fila horizontal desplazable en lugar de columna vertical */
  .daterangepicker .ranges {
    flex: 0 0 100% !important;
    width: 100% !important;
    border-right: none !important;
    border-bottom: 1px solid #e4e7ec !important;
  }

  .daterangepicker .ranges ul {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    padding: 6px 8px !important;
    gap: 4px !important;
  }

  .daterangepicker .ranges ul li {
    padding: 5px 10px !important;
    font-size: 12px !important;
    border-radius: 20px !important;
    border: 1px solid #d0d5dd !important;
    background: #fff !important;
    white-space: nowrap !important;
  }

  .daterangepicker .ranges ul li.active {
    background: #107631 !important;
    color: #fff !important;
    border-color: #107631 !important;
  }

  /* El único calendario ocupa el ancho disponible */
  .daterangepicker .drp-calendar.left {
    flex: 1 1 auto !important;
    border-left: none !important;
    padding: 10px !important;
  }
}
</style>
